import React, { useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import { Heading, Text, Button, ProgressCircle } from "@react-spectrum/s2";
import { useNavigate, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EnsureUserResponse } from './types';
import { AppSidebar } from './components/ChatBox';
import { CommandCenterSidebar, OrgDetailSidebar } from './components/CommandCenter';
import { Navbar } from './components/Navbar';
import { AuthLoadingView } from './components/App/AuthLoadingView';
import { NoReadAccessView } from './components/App/NoReadAccessView';
import { ErrorBoundary } from './components/ErrorBoundary';
import { TermsDialog } from './components/App/TermsDialog';
import { useAppAuth } from './hooks/useAppAuth';
import { useIsMobile } from './hooks/useIsMobile';
import { useSidebarCollapsed } from './hooks/useSidebarCollapsed';
import { ensureUser, acceptTerms } from './services/api';
import { startProductTour } from './utils/productTour';
import { getErrorMessage } from './utils/errorUtils';
import { STORAGE_KEYS } from './config/config';
import { clearOAuthCallbackFromUrl } from './services/auth';
import { APP_NAME, ROUTES } from './constants';
import { SiteProvider, useSiteContext, CommandCenterView, isCommandCenterView, getCommandCenterSubView } from './contexts/SiteContext';
import { PermissionsProvider, usePermissionsContext } from './contexts/PermissionsContext';
import { ChatProvider, useChatContext } from './contexts/ChatContext';
import MainContent from './pages/MainContent';
import './components/App/App.css';
// Isolated full-bleed feature, lazy-loaded so its code (and the @dnd-kit it will
// use) never ships in the main app bundle for users who don't open it.
const TemplateDesignerApp = React.lazy(()=>import('./features/template-designer/TemplateDesignerApp'));
// Full-screen preview opened in its own browser tab; reads the editor's config
// snapshot from sessionStorage. Lazy so it stays out of the main bundle too.
const TemplatePreviewPage = React.lazy(()=>import('./features/template-designer/preview/PreviewPage'));
// ---------------------------------------------------------------------------
// Inner shell: reads from contexts to wire the Sidebar
// ---------------------------------------------------------------------------
const AppShell: React.FC<{
    auth: ReturnType<typeof useAppAuth>;
    orgDisplayName: string | null;
    selectedOrgId: string | null;
    onOrgSwitch: (orgId: string) => void;
}> = ({ auth, orgDisplayName, selectedOrgId, onOrgSwitch })=>{
    const navigate = useNavigate();
    const site = useSiteContext();
    const { userPermissions, orgPermissions, orgPublishingEnabled } = usePermissionsContext();
    const chat = useChatContext();
    const { t } = useTranslation();
    const handleLogout = useCallback(()=>{
        auth.logout();
        navigate(ROUTES.HOME, {
            replace: true
        });
    }, [
        auth,
        navigate
    ]);
    // Load the Flameback chat widget only after the user is authenticated.
    // The widget was previously embedded statically in public/index.html which
    // made the chat icon visible on the pre-login WelcomeView as well.
    useEffect(()=>{
        if (!auth.isAuthenticated) return;
        const WIDGET_SRC = 'https://guidesai.adobe.io/widget/flameback-widget-loader.js?apiKey=fb_sk_HDeuQFJY0y7n9YawVZPuoxv9N4PINruxEj4BCfcNRCQ';
        const WIDGET_ATTR = 'data-flameback-widget';
        if (document.querySelector(`script[${WIDGET_ATTR}]`)) return;
        const removeWidget = ()=>{
            document.getElementById('fb-widget-loader-root')?.remove();
        };
        const script = document.createElement('script');
        script.src = WIDGET_SRC;
        script.defer = true;
        script.setAttribute(WIDGET_ATTR, 'true');
        script.addEventListener('load', ()=>{
            // A detached script can still finish loading after logout.
            if (!document.querySelector(`script[${WIDGET_ATTR}]`)) removeWidget();
        }, {
            once: true
        });
        document.body.appendChild(script);
        return ()=>{
            script.remove();
            removeWidget();
        };
    }, [
        auth.isAuthenticated
    ]);
    // Determine if we're in Command Center based on activeView
    const isCommandCenter = isCommandCenterView(site.activeView);
    const commandCenterSubView = getCommandCenterSubView(site.activeView);
    // ---- Mobile navigation drawer ----
    // At the mobile breakpoint the fixed-width left rail would squeeze the main
    // content and force horizontal overflow, so it collapses into an off-canvas
    // drawer toggled from the navbar hamburger. Command Center's own sidebars
    // (CommandCenterSidebar / OrgDetailSidebar) use the same one-level mobile
    // nav pattern as AppSidebar, so they share this same drawer toggle.
    const isMobile = useIsMobile();
    const [navOpen, setNavOpen] = useState(false);
    const showMobileNav = auth.isAuthenticated && isMobile;
    // ---- Desktop primary-rail collapse ----
    // On desktop the leftmost nav rail can be toggled between full (icons + labels)
    // and a compact icon-only rail. The choice is persisted so it survives reloads.
    // Mobile ignores this flag — AppSidebar renders the off-canvas drawer with full
    // labels regardless (see `collapsed` wiring below).
    const { collapsed: sidebarCollapsed, toggle: toggleSidebarCollapsed } = useSidebarCollapsed();
    // Close the drawer after a navigation choice (activeView / site change) so it
    // never lingers over the destination the user just picked.
    useEffect(()=>{
        setNavOpen(false);
    }, [
        site.activeView,
        site.selectedSite?.site_id
    ]);
    // Chat-history master/detail: picking a conversation from the history list
    // (which lives in the drawer at this breakpoint) changes the active
    // conversation without changing activeView, so close the drawer here too to
    // reveal the selected chat (the "detail") full-width.
    useEffect(()=>{
        setNavOpen(false);
    }, [
        chat.activeConversationId
    ]);
    // Command Center's domain master/detail: picking a domain from the Domains
    // list (in OrgDetailSidebar) doesn't change activeView either, so close the
    // drawer the same way to reveal the selected domain's detail full-width.
    useEffect(()=>{
        setNavOpen(false);
    }, [
        site.selectedDomainSiteId
    ]);
    // Never leave the drawer "open" state stuck when returning to desktop.
    useEffect(()=>{
        if (!showMobileNav) setNavOpen(false);
    }, [
        showMobileNav
    ]);
    const closeMobileNav = useCallback(()=>setNavOpen(false), []);
    const toggleMobileNav = useCallback(()=>setNavOpen((open)=>!open), []);
    const renderSidebar = (): React.ReactNode =>{
        if (!auth.isAuthenticated) return null;
        if (isCommandCenter) {
            if (site.activeView === 'commandCenter' && site.selectedOrg) {
                return (<OrgDetailSidebar key="org-detail-sidebar" orgId={site.selectedOrg.org_id} orgName={site.selectedOrg.org_name} activeSection={site.activeNavSection} domainsPanelOpen={site.domainsPanelOpen} selectedDomainSiteId={site.selectedDomainSiteId} onBack={site.handleBackFromOrgDetail} onToggleDomains={site.handleToggleDomainsPanel} onSection={site.handleOrgDetailSection} onSelectDomain={site.handleSelectDomain} isDrawerOpen={navOpen} onMobileNavClose={closeMobileNav}/>);
            }
            return (<CommandCenterSidebar key="command-center-sidebar" activeView={commandCenterSubView} onBack={site.handleHomeView} onOrganizations={site.handleCommandCenter} onDefaultOrgConfig={site.handleDefaultOrgConfig} onModels={site.handleModels} isDrawerOpen={navOpen} onMobileNavClose={closeMobileNav}/>);
        }
        return (<AppSidebar key="app-sidebar" sites={site.sites} loading={site.sitesLoading} error={site.sitesError} selectedSite={site.selectedSite} activeView={site.activeView as Exclude<typeof site.activeView, CommandCenterView>} userPermissions={userPermissions} orgPermissions={orgPermissions} orgPublishingEnabled={orgPublishingEnabled} activeConversationId={chat.activeConversationId} onSelectContext={site.handleSelectContext} onBack={site.handleBack} onNewChat={chat.handleNewChat} onStartChat={chat.handleStartChat} onDelete={site.handleDeleteSite} onAddContent={site.handleAddContent} onStats={site.handleStats} onAnalytics={site.handleAnalytics} onOpenCreate={site.handleOpenCreate} onSettings={site.handleSettings} onPublishing={site.handlePublishing} onTools={site.handleTools} onHistory={site.handleHistory} onResumeChat={chat.handleResumeChat} onSecurity={site.handleSecurity} onSampleContent={site.handleSampleContent} onDocumentation={site.handleDocumentation} onModels={site.handleOrgModels} onClearOrgView={site.handleHomeView} isDrawerOpen={navOpen} onMobileNavClose={closeMobileNav} collapsed={sidebarCollapsed}/>);
    };
    if (!auth.isAuthenticated) {
        return (<MainContent isAuthenticated={false} authError={auth.error} onLogin={auth.login}/>);
    }
    return (<div className={"app-root" + " Mc171 Fc171"}>
      <Navbar user={auth.user} isAuthenticated={auth.isAuthenticated} onLogout={handleLogout} onStartTour={auth.isAuthenticated && !site.selectedSite ? startProductTour : undefined} orgName={orgDisplayName} userOrgIds={auth.userOrgIds} orgNames={auth.orgNames} selectedOrgId={selectedOrgId} onOrgSwitch={onOrgSwitch} onHomeClick={site.handleBack} orgPermissions={orgPermissions} onCommandCenter={site.handleCommandCenter} onToggleNav={showMobileNav ? toggleMobileNav : auth.isAuthenticated && !isCommandCenter ? toggleSidebarCollapsed : undefined} isNavOpen={showMobileNav ? navOpen : !sidebarCollapsed} navToggleVariant={showMobileNav ? 'drawer' : 'collapse'} showOrgPicker={!showMobileNav}/>

      <div className={`app-body${navOpen ? ' app-body--nav-open' : ''}` + " _ub171 _vb171 _sb171"}>
        {showMobileNav && navOpen && (<button type="button" className="app-nav-backdrop" aria-label={t('nav.closeNav')} onClick={closeMobileNav}/>)}
        {renderSidebar()}

        <div className={"app-main-column" + " _ub171 _vb171 _sb171"}>
          <div data-tour="main-content" className="app-main-content">
            <MainContent isAuthenticated={auth.isAuthenticated} authError={auth.error} onLogin={auth.login} currentUserId={auth.user?.userId}/>
          </div>
        </div>
      </div>
    </div>);
};
// ---------------------------------------------------------------------------
// Root App: auth gate, terms gate, context providers
// ---------------------------------------------------------------------------
function App() {
    const auth = useAppAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    useEffect(()=>{
        document.title = APP_NAME;
    }, []);
    // ---- Selected org (persisted in localStorage) ----
    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(()=>{
        const stored = localStorage.getItem(STORAGE_KEYS.SELECTED_ORG);
        return stored || null;
    });
    // Sync selectedOrgId when userOrgIds become available (login / page refresh).
    // Skip while auth is still loading to avoid clearing the localStorage-restored
    // value before userOrgIds has been populated.
    useEffect(()=>{
        if (auth.isLoading) return;
        if (auth.userOrgIds.length === 0) {
            setSelectedOrgId(null);
            return;
        }
        // If the stored value is still valid, keep it; otherwise default to first
        if (selectedOrgId && auth.userOrgIds.includes(selectedOrgId)) return;
        const defaultOrg = auth.userOrgIds[0];
        setSelectedOrgId(defaultOrg);
        localStorage.setItem(STORAGE_KEYS.SELECTED_ORG, defaultOrg);
    }, [
        auth.isLoading,
        auth.userOrgIds
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    const handleOrgSwitch = useCallback((orgId: string)=>{
        clearOAuthCallbackFromUrl();
        setSelectedOrgId(orgId);
        localStorage.setItem(STORAGE_KEYS.SELECTED_ORG, orgId);
        setTermsAccepted(null);
        setHasReadAccess(null);
        setOrgAdminEmail(null);
        setOrgAdminName(null);
        setEnsureUserError(null);
        navigate(ROUTES.HOME, {
            replace: true
        });
    }, [
        navigate
    ]);
    // ---- User ensure + Terms & Conditions gate ----
    const [termsAccepted, setTermsAccepted] = useState<boolean | null>(null);
    const [hasReadAccess, setHasReadAccess] = useState<boolean | null>(null);
    const [orgAdminEmail, setOrgAdminEmail] = useState<string | null>(null);
    const [orgAdminName, setOrgAdminName] = useState<string | null>(null);
    const [userEnsureLoading, setUserEnsureLoading] = useState(false);
    const [ensureUserError, setEnsureUserError] = useState<string | null>(null);
    const retryEnsureUser = useCallback(async ()=>{
        if (!selectedOrgId) return;
        setUserEnsureLoading(true);
        setEnsureUserError(null);
        setHasReadAccess(null);
        try {
            const result: EnsureUserResponse = await ensureUser(selectedOrgId);
            setHasReadAccess(result.has_read_access);
            setOrgAdminEmail(result.org_admin_email ?? null);
            setOrgAdminName(result.org_admin_name ?? null);
            setTermsAccepted(result.terms_accepted);
        } catch (err) {
            console.error('Failed to ensure user:', err);
            setEnsureUserError(getErrorMessage(err, t('app.ensureUserError.initFailed')));
        } finally{
            setUserEnsureLoading(false);
        }
    }, [
        selectedOrgId,
        t
    ]);
    useEffect(()=>{
        if (!auth.isAuthenticated || !selectedOrgId) {
            setTermsAccepted(null);
            setHasReadAccess(null);
            setOrgAdminEmail(null);
            setOrgAdminName(null);
            setEnsureUserError(null);
            return;
        }
        let cancelled = false;
        const run = async ()=>{
            setUserEnsureLoading(true);
            setEnsureUserError(null);
            try {
                const result: EnsureUserResponse = await ensureUser(selectedOrgId);
                if (!cancelled) {
                    setHasReadAccess(result.has_read_access);
                    setOrgAdminEmail(result.org_admin_email ?? null);
                    setOrgAdminName(result.org_admin_name ?? null);
                    setTermsAccepted(result.terms_accepted);
                }
            } catch (err) {
                console.error('Failed to ensure user:', err);
                if (!cancelled) setEnsureUserError(getErrorMessage(err, t('app.ensureUserError.initFailed')));
            } finally{
                if (!cancelled) setUserEnsureLoading(false);
            }
        };
        run();
        return ()=>{
            cancelled = true;
        };
    }, [
        auth.isAuthenticated,
        selectedOrgId,
        t
    ]);
    const handleAcceptTerms = useCallback(async ()=>{
        if (!selectedOrgId) return;
        try {
            await acceptTerms(selectedOrgId);
            setTermsAccepted(true);
        } catch (err) {
            console.error('Failed to accept terms:', err);
        }
    }, [
        selectedOrgId
    ]);
    const handleDeclineTerms = useCallback(()=>{
        auth.logout();
        navigate(ROUTES.HOME, {
            replace: true
        });
    }, [
        auth,
        navigate
    ]);
    const handleLogout = useCallback(()=>{
        auth.logout();
        navigate(ROUTES.HOME, {
            replace: true
        });
    }, [
        auth,
        navigate
    ]);
    const orgDisplayName = useMemo(()=>{
        if (!selectedOrgId) return null;
        return auth.orgNames[selectedOrgId] ?? selectedOrgId;
    }, [
        selectedOrgId,
        auth.orgNames
    ]);
    // Wrap selectedOrgId in an array for providers that expect string[]
    const selectedOrgIds = useMemo(()=>selectedOrgId ? [
            selectedOrgId
        ] : [], [
        selectedOrgId
    ]);
    // ---- Early returns for loading / error / terms gates ----
    if (auth.isLoading) {
        return <AuthLoadingView/>;
    }
    // `selectedOrgId` is hydrated synchronously from localStorage on mount, so on
    // the first authenticated render it can already be non-null while the
    // ensureUser effect (which flips userEnsureLoading) hasn't fired yet --
    // gating on userEnsureLoading alone left a one-tick window where none of
    // these early returns matched and <Routes> (AppShell, including
    // URL-driven views like /models) rendered before hasReadAccess was known.
    // Gate on "authenticated with an org selected but no verdict yet" instead,
    // so nothing renders behind the gate until ensureUser resolves either way.
    if (auth.isAuthenticated && selectedOrgId && hasReadAccess === null && !ensureUserError) {
        return <AuthLoadingView/>;
    }
    if (auth.isAuthenticated && ensureUserError) {
        return (<div className={"app-root" + " Mc171 Fc171"}>
        <Navbar user={auth.user} isAuthenticated={auth.isAuthenticated} onLogout={handleLogout} orgName={orgDisplayName} userOrgIds={auth.userOrgIds} orgNames={auth.orgNames} selectedOrgId={selectedOrgId} onOrgSwitch={handleOrgSwitch} onHomeClick={()=>navigate(ROUTES.HOME, {
                replace: true
            })}/>
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1
        }} className={" Tk171 Qk171 Sk171 Rk171"}>
          <Heading level={3} styles={" Gv171"}>{t('app.ensureUserError.title')}</Heading>
          <Text styles={" GA171"}>{ensureUserError}</Text>
          <Button variant="primary" onPress={retryEnsureUser} isPending={userEnsureLoading}>
            {t('primitives.retry')}
          </Button>
        </div>
      </div>);
    }
    if (auth.isAuthenticated && hasReadAccess === false) {
        return (<div className={"app-root" + " Mc171 Fc171"}>
        <Navbar user={auth.user} isAuthenticated={auth.isAuthenticated} onLogout={handleLogout} orgName={orgDisplayName} userOrgIds={auth.userOrgIds} orgNames={auth.orgNames} selectedOrgId={selectedOrgId} onOrgSwitch={handleOrgSwitch} onHomeClick={()=>navigate(ROUTES.HOME, {
                replace: true
            })}/>
        <NoReadAccessView orgAdminEmail={orgAdminEmail} orgAdminName={orgAdminName}/>
      </div>);
    }
    if (auth.isAuthenticated && termsAccepted === false) {
        return (<div className={"app-root" + " Mc171 Fc171"}>
        <Navbar user={auth.user} isAuthenticated={auth.isAuthenticated} onLogout={handleLogout} orgName={orgDisplayName} userOrgIds={auth.userOrgIds} orgNames={auth.orgNames} selectedOrgId={selectedOrgId} onOrgSwitch={handleOrgSwitch} onHomeClick={()=>navigate(ROUTES.HOME, {
                replace: true
            })}/>
        <TermsDialog onAccept={handleAcceptTerms} onDecline={handleDeclineTerms}/>
      </div>);
    }
    // ---- Main authenticated (or unauthenticated) app ----
    //
    // Top-level route split: the Template Designer is a full-bleed, isolated
    // feature that bypasses the Navbar/Sidebar AppShell and the Site/Chat/
    // Permissions provider stack — it brings only its own TemplateDesignerProvider.
    // Every other path ("*") renders the existing AppShell exactly as before.
    const designerFallback = (<div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh'
    }}>
      <ProgressCircle aria-label={t('app.loadingTemplateDesigner')} isIndeterminate/>
    </div>);
    return (<Routes>
      <Route path={`${ROUTES.TEMPLATE_DESIGNER}/preview`} element={<ErrorBoundary>
            <Suspense fallback={designerFallback}>
              <TemplatePreviewPage/>
            </Suspense>
          </ErrorBoundary>}/>
      <Route path={`${ROUTES.TEMPLATE_DESIGNER}/:siteId/*`} element={<ErrorBoundary>
            <Suspense fallback={designerFallback}>
              <TemplateDesignerApp/>
            </Suspense>
          </ErrorBoundary>}/>
      <Route path={`${ROUTES.TEMPLATE_DESIGNER}/*`} element={<ErrorBoundary>
            <Suspense fallback={designerFallback}>
              <TemplateDesignerApp/>
            </Suspense>
          </ErrorBoundary>}/>
      <Route path="*" element={<ErrorBoundary>
            <SiteProvider userOrgIds={selectedOrgIds} isAuthenticated={auth.isAuthenticated}>
              <PermissionsProvider isAuthenticated={auth.isAuthenticated} userOrgIds={selectedOrgIds}>
                <ChatProvider>
                  <AppShell auth={auth} orgDisplayName={orgDisplayName} selectedOrgId={selectedOrgId} onOrgSwitch={handleOrgSwitch}/>
                </ChatProvider>
              </PermissionsProvider>
            </SiteProvider>
          </ErrorBoundary>}/>
    </Routes>);
}
export default App;

import "macro-0ce73200fd6e271527a168b6979710c59e8dec693d49f59bc76a946a1da2cbe1.css";
import "macro-bab5ef82f09fb5d0f4436f805cbbf1506e94fd514257b2e4f43932c802b98f56.css";
import "macro-bab5ef82f09fb5d0f4436f805cbbf1506e94fd514257b2e4f43932c802b98f56.css";
import "macro-0ce73200fd6e271527a168b6979710c59e8dec693d49f59bc76a946a1da2cbe1.css";
import "macro-e67ea437b21703bf49bd8ed98ce85f2a5f6fef700f2e266811ec77a2f06c61d3.css";
import "macro-5641efd6cdcbecf0cb83d2303423673566d2e738af59fa0ad1cd5fab470be4d2.css";
import "macro-08e5f7bb3f7d282fb10251c78ed631526831cfbc11edeed2ef36b94ec669573f.css";
import "macro-0ce73200fd6e271527a168b6979710c59e8dec693d49f59bc76a946a1da2cbe1.css";
import "macro-0ce73200fd6e271527a168b6979710c59e8dec693d49f59bc76a946a1da2cbe1.css";